import { ReportStats, ReportType } from '@/lib/api/reports/reports-api'
import {
    MdAccountBalance,
    MdArrowDownward,
    MdArrowUpward,
    MdAttachMoney,
    MdCake,
    MdPayments,
    MdPeople,
    MdPercent,
    MdReceiptLong,
    MdTrendingFlat,
    MdTrendingUp,
} from 'react-icons/md'
import { ReportConfig } from '../config/report-configs'
import { KpiCard, KpiSkeleton } from './kpi-card'

const formatBRL = (centavos: any) => {
    const value = Number(centavos) || 0
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100)
}

/** Tipos com custo/lucro apurados. Espelha `COST_TYPES` do backend. */
const COST_TYPES: ReportType[] = ['consultations', 'exams', 'orders', 'billings']

interface ReportStatsProps {
    stats: ReportStats | null
    config: ReportConfig
    loading: boolean
    reportType?: ReportType
    /** Aceita lista desde que os filtros viraram multi-seleção. */
    activePaymentStatus?: string[] | string
    onPaymentStatusClick?: (status: string[] | string | undefined) => void
    /**
     * Filtro "Tipo de lançamento" (`RECEIVABLE`/`PAYABLE`) em vigor. Com o recorte
     * preso a um dos lados, os cards do outro lado deixam de ter conteúdo — ver
     * `sidesInScope`.
     */
    activeBillingKind?: string[] | string
    /** Filtro de status da cobrança em vigor. Muda o RÓTULO dos cards — ver `settledOnly`. */
    activeBillingStatus?: string[] | string
}

/**
 * KPIs de Custo/Lucro/Margem. Linhas sem snapshot de repasse ficam fora dos totais —
 * o `hint` explicita quantas, senão o leitor assume que o custo cobre tudo.
 */
function CostKpis({ stats }: { stats: ReportStats }) {
    const unknown = stats.costUnknownCount ?? 0
    const known = stats.costKnownCount ?? 0
    if (known === 0) return null

    const profit = stats.totalProfit ?? 0
    const hint = unknown > 0 ? `${unknown} sem custo apurado` : undefined

    return (
        <>
            <KpiCard
                title="Custo Total"
                value={formatBRL(stats.totalCost)}
                icon={<MdPayments />}
                accent="warning"
                hint={hint}
            />
            <KpiCard
                title="Lucro Total"
                value={formatBRL(profit)}
                icon={profit < 0 ? <MdArrowDownward /> : <MdTrendingUp />}
                accent={profit < 0 ? 'error' : 'success'}
                hint={hint}
            />
            {stats.profitMargin != null && (
                <KpiCard
                    title="Margem"
                    value={`${stats.profitMargin.toString().replace('.', ',')}%`}
                    icon={<MdPercent />}
                    accent={stats.profitMargin < 0 ? 'error' : 'info'}
                    hint={`sobre ${known} ${known === 1 ? 'registro' : 'registros'}`}
                />
            )}
        </>
    )
}

/**
 * Que lados do caixa o recorte atual comporta.
 *
 * Filtrando "A Pagar", "A Receber" vinha R$ 0,00 (não há recebível no recorte) e o
 * Saldo Líquido vinha exatamente o total a pagar com o sinal trocado — três cards para
 * dizer um número só, dois deles sem informação nenhuma. Sem filtro, os dois lados
 * existem e o saldo é a conta que importa.
 *
 * A decisão vem do FILTRO, não do valor: um recorte de recebíveis pode somar zero
 * legitimamente (cobrança de convênio nasce com `amount` zerado) e o card precisa
 * continuar ali dizendo isso.
 */
function sidesInScope(kind?: string[] | string) {
    const list = Array.isArray(kind) ? kind : kind ? [kind] : []
    if (list.length === 0) return { receivable: true, payable: true }
    return {
        receivable: list.includes('RECEIVABLE'),
        payable: list.includes('PAYABLE'),
    }
}

/**
 * O recorte é só de cobrança PAGA?
 *
 * Muda o que os números significam, e por isso muda o rótulo. `totalReceivable` soma
 * todo `RECEIVABLE` do recorte, esteja pendente ou não — com o filtro em "Pago", o card
 * "A Receber" mostrava dinheiro que JÁ ENTROU, e "a receber" se lê como pendência. Com
 * o recorte fechado em pago, os nomes certos são Receita e Despesa, e a diferença entre
 * eles é Resultado, não "saldo a realizar".
 */
function settledOnly(status?: string[] | string): boolean {
    const list = Array.isArray(status) ? status : status ? [status] : []
    return list.length > 0 && list.every(value => value === 'PAID')
}

export default function ReportStatsComponent({
    stats,
    config,
    loading,
    reportType,
    activeBillingKind,
    activeBillingStatus,
}: ReportStatsProps) {
    const isBillings = reportType === 'billings'
    const showCost = reportType != null && COST_TYPES.includes(reportType)

    if (loading) {
        return (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[...Array(4)].map((_, i) => <KpiSkeleton key={i} />)}
            </div>
        )
    }

    if (!stats) return null

    const showFinancial = config.hasFinancial

    if (isBillings) {
        const net = stats.netBalance ?? 0
        const netAccent = net > 0 ? 'success' : net < 0 ? 'error' : 'neutral'
        const netIcon = net > 0 ? <MdTrendingUp /> : net < 0 ? <MdArrowDownward /> : <MdTrendingFlat />
        const sides = sidesInScope(activeBillingKind)
        // O saldo só é uma informação nova quando há os dois lados para subtrair.
        const showNet = sides.receivable && sides.payable
        const realizado = settledOnly(activeBillingStatus)
        return (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <KpiCard
                    title="Total de Lançamentos"
                    value={stats.total}
                    icon={<MdReceiptLong />}
                    accent="primary"
                />
                {sides.receivable && (
                    <KpiCard
                        title={realizado ? 'Receita' : 'A Receber'}
                        value={formatBRL(stats.totalReceivable)}
                        icon={<MdArrowUpward />}
                        accent="success"
                        hint={realizado ? 'já recebida' : undefined}
                    />
                )}
                {sides.payable && (
                    <KpiCard
                        title={realizado ? 'Despesa' : 'A Pagar'}
                        value={formatBRL(stats.totalPayable)}
                        icon={<MdArrowDownward />}
                        accent="error"
                        hint={realizado ? 'já paga' : undefined}
                    />
                )}
                {showNet && (
                    <KpiCard
                        title={realizado ? 'Resultado' : 'Saldo Líquido'}
                        value={formatBRL(net)}
                        icon={netIcon}
                        accent={netAccent}
                        hint={realizado ? 'receita − despesa' : undefined}
                    />
                )}
                <CostKpis stats={stats} />
            </div>
        )
    }

    // Outros relatorios: total + receita + ticket medio + top status
    const statusEntries = Object.entries(stats.byStatus)
    const topStatus = statusEntries.sort((a, b) => b[1] - a[1])[0]
    const isClients = reportType === 'clients'

    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <KpiCard
                title={isClients ? 'Total de Clientes' : 'Total'}
                value={stats.total}
                icon={<MdReceiptLong />}
                accent="primary"
            />
            {showFinancial && (
                <>
                    {/* "Vendido", o mesmo termo e a mesma conta do dashboard financeiro
                        (`netRevenue`, pela base de período escolhida em "Contar período por"). */}
                    <KpiCard
                        title="Vendido"
                        value={formatBRL(stats.totalRevenue)}
                        icon={<MdAccountBalance />}
                        accent="success"
                    />
                    <KpiCard
                        title="Ticket Médio"
                        value={formatBRL(stats.averageTicket)}
                        icon={<MdAttachMoney />}
                        accent="info"
                    />
                </>
            )}
            {showCost && <CostKpis stats={stats} />}
            {isClients && (
                <>
                    <KpiCard
                        title="Gasto Médio"
                        value={formatBRL(stats.averageSpend)}
                        icon={<MdAttachMoney />}
                        accent="success"
                    />
                    <KpiCard
                        title="Gasto Total"
                        value={formatBRL(stats.totalSpend)}
                        icon={<MdAccountBalance />}
                        accent="info"
                    />
                    <KpiCard
                        title="Média de Idade"
                        value={stats.averageAge ? `${stats.averageAge} anos` : '-'}
                        icon={<MdCake />}
                        accent="warning"
                    />
                </>
            )}
            {topStatus && (
                <KpiCard
                    title={isClients ? 'Gênero predominante' : `Maior status: ${topStatus[0]}`}
                    value={isClients ? topStatus[0] : topStatus[1]}
                    icon={isClients ? <MdPeople /> : <MdTrendingUp />}
                    accent="warning"
                />
            )}
        </div>
    )
}
